import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, User, MeResponse, onUnauthorized } from '../api';

interface AuthUser extends User {
  influencer?: {
    id: number;
    stage_name: string;
    first_name: string;
    last_name: string;
  };
  vendor?: {
    id: number;
    name: string;
    vendor_id: string;
    preferred_locale: string;
  };
}

interface LoginResult {
  success: boolean;
  requiresVerification?: boolean;
  message?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string, turnstileToken: string) => Promise<LoginResult>;
  verifyAdminCode: (email: string, code: string, turnstileToken?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();

  const refresh = useCallback(async () => {
    try {
      const userData: MeResponse = await api.getMe();
      setUser({
        id: userData.id,
        email: userData.email,
        name: userData.name,
        role: userData.role,
        admin_level: userData.admin_level,
        storefront_id: userData.storefront_id,
        vendor_id: userData.vendor_id,
        influencer: userData.influencer,
        vendor: userData.vendor,
      });
      if (userData.vendor?.preferred_locale) {
        i18n.changeLanguage(userData.vendor.preferred_locale);
      }
    } catch {
      setUser(null);
    }
  }, [i18n]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // Global 401 handler: if any authenticated request returns 401 (session
  // expired or invalidated server-side), clear user state and bounce to /login.
  // Auth-flow endpoints opt out of this via AUTH_FLOW_PATHS in api.ts so the
  // login page itself can still render 401s inline without redirecting.
  useEffect(() => {
    const off = onUnauthorized(() => {
      setUser(null);
      const path = window.location.pathname;
      // Public surfaces — never bounce a guest off these to /login. The
      // landing page (/) and any legal/legacy-home pages are reachable
      // without an account by design.
      const isPublic =
        path === '/' ||
        path === '/login' || path === '/admin/login' || path === '/vendor/login' ||
        path === '/signup' || path === '/forgot-password' || path === '/reset-password' ||
        path === '/legacy-home' ||
        path.startsWith('/legal/') ||
        path.startsWith('/accept-invite') || path.startsWith('/invite') ||
        path.startsWith('/vendor/register') || path.startsWith('/vendor/accept-invite');
      if (!isPublic) {
        window.location.assign('/login');
      }
    });
    return off;
  }, []);

  /**
   * Login with email, password, and Turnstile token
   * For admin users, this returns requiresVerification = true
   * The caller should then redirect to the 2FA verification page
   */
  const login = async (
    email: string,
    password: string,
    turnstileToken: string
  ): Promise<LoginResult> => {
    const result = await api.login(email, password, turnstileToken);

    if (result.requiresVerification) {
      // Admin needs 2FA
      return {
        success: false,
        requiresVerification: true,
        message: result.message,
      };
    }

    // Influencer or admin (if 2FA disabled) - login complete
    if (result.user) {
      await refresh();
      return { success: true };
    }

    throw new Error('Unexpected login response');
  };

  /**
   * Verify admin 2FA code and complete login
   */
  const verifyAdminCode = async (email: string, code: string, turnstileToken?: string) => {
    await api.verifyAdminCode(email, code, turnstileToken);
    await refresh();
  };

  const logout = async () => {
    await api.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyAdminCode, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
